import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState
} from 'react';
import Select, { ActionMeta, MultiValue, SingleValue } from 'react-select';
import { Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { twMerge } from 'tailwind-merge';

import { MenuMaster, RoleMasterForm } from 'models/role';

import { useMasterMenu } from '../../WorkflowMenuMasterConfig/queries';
import { MasterCompany } from '../../../../models/company/MasterCompany';
import { useAdminCompanies } from '../../../../hooks';

export type WorkflowRoleMasterFormRefHandle = {
    submit: () => RoleMasterForm | undefined;
};

export type WorkflowRoleMasterFormProps = {
    disabledKeyField?: boolean;
    defaultValues?: RoleMasterForm;
    className?: string;
    onSubmit?: (formValues: RoleMasterForm) => void;
};

export const WorkflowRoleMasterForm = forwardRef<
    WorkflowRoleMasterFormRefHandle,
    WorkflowRoleMasterFormProps
>((props, ref) => {
    const { disabledKeyField, defaultValues, className } = props;

    const [formValues, setFormValues] = useState<RoleMasterForm>({
        sid: defaultValues?.sid ?? '',
        id: defaultValues?.id ?? '',
        roleDetail: defaultValues?.roleDetail ?? '',
        allowMenus: defaultValues?.allowMenus ?? []
    });
    const [errors, setErrors] = useState<Record<keyof RoleMasterForm, string>>(
        // @ts-ignore
        {}
    );

    const { adminCompanies, isAllowedAllCompanies } = useAdminCompanies();
    const adminCompanyOptions = useMemo(() => {
        if (!adminCompanies) {
            return undefined;
        }

        if (isAllowedAllCompanies) {
            const selectAllOption = {
                sid: '*',
                company_name: '*'
            } as MasterCompany;
            return [selectAllOption, ...adminCompanies];
        }

        return adminCompanies;
    }, [adminCompanies, isAllowedAllCompanies]);

    const { data: masterMenu } = useMasterMenu({
        enabled: !!formValues.sid,
        sid: formValues.sid
    });
    const menuOptions = useMemo(() => {
        const selectAllOptions = { id: 'เลือกทั้งหมด' } as MenuMaster;
        return masterMenu ? [selectAllOptions, ...masterMenu] : undefined;
    }, [masterMenu]);

    const getMasterMenuLabel = useCallback(
        (masterMenu: MenuMaster) =>
            //masterMenu.id +
            //(masterMenu.menu_name ? `: ${masterMenu.menu_name}` : ''),
            (masterMenu.menu_name ? `${masterMenu.menu_name}` : ''),
        []
    );

    const getMasterMenuValue = useCallback(
        (masterMenu: MenuMaster) => masterMenu.id,
        []
    );

    const handleFormValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const key = event.currentTarget.name;
        const value = event.currentTarget.value;

        setFormValues({ ...formValues, [key]: value });
    };

    const handleSelectedCompany = (
        newSelectedCompany: SingleValue<MasterCompany>
    ) => {
        if (!newSelectedCompany) {
            return;
        }

        setFormValues({
            ...formValues,
            sid: newSelectedCompany.sid
        });
    };

    const handleSelectedMenusChange = (
        newSelectedMenus: MultiValue<MenuMaster>,
        actionMeta: ActionMeta<MenuMaster>
    ) => {
        if (
            actionMeta.action === 'select-option' &&
            actionMeta.option?.id === 'เลือกทั้งหมด'
        ) {
            setFormValues({
                ...formValues,
                allowMenus: menuOptions?.slice(1) ?? []
            });
            return;
        }

        setFormValues({
            ...formValues,
            // @ts-ignore
            allowMenus: newSelectedMenus
        });
    };

    const submitForm = () => {
        const errors: [keyof RoleMasterForm, string][] = [];

        if (!formValues.sid) {
            errors.push(['sid', 'กรุณาเลือกบริษัท']);
        }

        if (!formValues.id) {
            errors.push(['id', 'กรุณากรอก Role ID']);
        }

        if (errors.length > 0) {
            setErrors(prevState => ({
                ...prevState,
                // @ts-ignore
                ...Object.fromEntries(errors)
            }));

            return undefined;
        }

        return { ...formValues };
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: submitForm
        }),
        [submitForm]
    );

    return (
        <Form className={twMerge('flex flex-col', className)}>
            <FormGroup>
                <Label for="sid">Company</Label>
                <Select
                    isDisabled={disabledKeyField}
                    options={adminCompanyOptions}
                    value={adminCompanyOptions?.find(
                        companyOption => companyOption.sid === formValues.sid
                    )}
                    getOptionLabel={(company: MasterCompany) =>
                        company.company_name
                    }
                    getOptionValue={(company: MasterCompany) => company.sid}
                    onChange={handleSelectedCompany}
                />
                <FormFeedback>{errors.sid}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="id">Role ID</Label>
                <Input
                    disabled={disabledKeyField}
                    invalid={!!errors.id}
                    id="id"
                    type="text"
                    name="id"
                    value={formValues.id}
                    onChange={
                        !disabledKeyField ? handleFormValueChange : undefined
                    }
                />
                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="menu_name">Allowed Menu</Label>
                <Select
                    isMulti
                    isDisabled={!formValues.sid}
                    options={menuOptions}
                    value={formValues.allowMenus}
                    getOptionLabel={getMasterMenuLabel}
                    getOptionValue={getMasterMenuValue}
                    onChange={handleSelectedMenusChange}
                />
                <FormFeedback>{errors.allowMenus}</FormFeedback>
            </FormGroup>
        </Form>
    );
});
